/* ═════════════════════════════════════════════════════════════════
   THE VINCI ORACLE · Neural Command Interface
   ═════════════════════════════════════════════════════════════════
   • Vinci-Eye monogram launcher with rotating orbital rings
   • Black-hole warp entry animation
   • Ultra-glassmorphism window (bioluminescent gold heartbeat)
   • Particle-dust field that reacts to mouse
   • Data-Pulse 3D wave instead of "typing…"
   • Holographic teleport fade-in per assistant message
   • Cinzel typography for the Oracle name
   • Holographic Price Plate from get_price tool events
   • **Vinci Code Access Gate**: guests get general Q&A only; verified
     5151 partners unlock live pricing. 24-hour session persistence.
   • Preserves SSE streaming, WebSpeech voice, guest rate-limit,
     draft-RFQ toast.
   ═════════════════════════════════════════════════════════════════ */
import { useState, useRef, useEffect, useCallback, useMemo } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { toast } from "sonner";
import {
  X, Send, Loader2, Sparkles, BadgeCheck, Mic, MicOff, TrendingUp,
  KeyRound, ShieldCheck, LogOut, ShieldAlert,
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const SS_SID = "vinci_exec_sid";
const LS_AUTH = "vinci_oracle_auth";   // { vinci_code, partner_name, business_name, verified_until }

const SpeechRecognitionCtor =
  typeof window !== "undefined"
    ? window.SpeechRecognition || window.webkitSpeechRecognition
    : null;
const VOICE_SUPPORTED = !!SpeechRecognitionCtor;

/* ── Auth helpers (24h persistence) ─────────────────────────────── */
function loadAuth() {
  try {
    const raw = localStorage.getItem(LS_AUTH);
    if (!raw) return null;
    const a = JSON.parse(raw);
    if (!a?.vinci_code || !a?.verified_until) return null;
    if (new Date(a.verified_until).getTime() < Date.now()) {
      localStorage.removeItem(LS_AUTH);
      return null;
    }
    return a;
  } catch { return null; }
}
function saveAuth(a) {
  try { localStorage.setItem(LS_AUTH, JSON.stringify(a)); } catch { /* silent */ }
}
function clearAuth() {
  try { localStorage.removeItem(LS_AUTH); } catch { /* silent */ }
}

/* ── Particle-dust field ────────────────────────────────────────── */
const DUST_SEEDS = Array.from({ length: 16 }).map((_, i) => ({
  id: i,
  bx: [8, 22, 38, 54, 68, 82, 92, 14, 30, 46, 62, 78, 88, 18, 50, 74][i],
  by: [12, 62, 24, 78, 36, 88, 48, 92, 8, 50, 20, 68, 32, 44, 82, 6][i],
  size: 1.6 + (i % 3) * 0.6,
  delay: (i * 137) % 3200,
  duration: 3200 + ((i * 91) % 2400),
  dx: ((i % 5) - 2) * 8,
  dy: ((i % 4) - 2) * 7,
  op: 0.35 + ((i % 5) * 0.09),
}));

function ParticleField({ mouse }) {
  return (
    <div className="pointer-events-none absolute inset-0 overflow-hidden">
      {DUST_SEEDS.map((d) => {
        const mx = mouse?.x ?? null;
        const my = mouse?.y ?? null;
        let cursorX = 0, cursorY = 0;
        if (mx !== null && my !== null) {
          const dx = mx - d.bx;
          const dy = my - d.by;
          const dist = Math.hypot(dx, dy) + 1;
          const pull = Math.min(14, 220 / dist);
          cursorX = (dx / dist) * pull;
          cursorY = (dy / dist) * pull;
        }
        return (
          <span
            key={d.id}
            className="absolute rounded-full"
            style={{
              left: `${d.bx}%`, top: `${d.by}%`,
              width: `${d.size}px`, height: `${d.size}px`,
              background: "radial-gradient(circle, #F6E4A6 0%, rgba(216,178,62,0.6) 55%, transparent 100%)",
              boxShadow: "0 0 6px rgba(246,228,166,0.9)",
              transform: `translate(${cursorX}px, ${cursorY}px)`,
              transition: "transform 480ms cubic-bezier(.16,.84,.32,1)",
              ["--tx"]: `${cursorX}px`,
              ["--ty"]: `${cursorY}px`,
              ["--dx"]: `${d.dx}px`,
              ["--dy"]: `${d.dy}px`,
              ["--op"]: d.op,
              animation: `oracle-dust-float ${d.duration}ms ease-in-out ${d.delay}ms infinite`,
            }}
          />
        );
      })}
    </div>
  );
}

function DataPulseWave() {
  return (
    <div className="flex items-center gap-3 px-1 py-2">
      <div className="flex items-end gap-[3px] h-6" aria-label="Oracle is thinking">
        {[0, 1, 2, 3, 4, 5, 6, 7].map((i) => (
          <span
            key={i}
            className="w-[3px] rounded-full"
            style={{
              height: "100%",
              background: "linear-gradient(180deg, #F6E4A6 0%, #D8B23E 55%, #7A5B14 100%)",
              transformOrigin: "center",
              animation: `oracle-wave 1.15s ease-in-out ${i * 110}ms infinite`,
              boxShadow: "0 0 8px rgba(216,178,62,0.6)",
            }}
          />
        ))}
      </div>
      <span className="font-cinzel text-[9px] uppercase text-[#D8B23E]">
        Oracle consulting the synchronized core
      </span>
    </div>
  );
}

function HolographicPricePlate({ priceEvent }) {
  const r = priceEvent?.result || {};
  if (!r?.found) return null;
  const price = r.price_inr_per_unit;
  const unit = r.unit || "MT";
  const label = r.brand || (priceEvent.args?.brand)
    || (r.category || "").replace(/_/g, " ").toUpperCase();
  const inr = typeof price === "number"
    ? "₹ " + price.toLocaleString("en-IN")
    : null;
  if (!inr) return null;
  return (
    <div className="mt-3 relative overflow-hidden rounded-sm border border-[#D8B23E]/50 oracle-teleport"
         style={{
           background: "linear-gradient(135deg, rgba(216,178,62,0.14) 0%, rgba(10,9,6,0.85) 60%, rgba(216,178,62,0.08) 100%)",
           boxShadow: "0 0 0 1px rgba(216,178,62,0.25), 0 8px 30px -12px rgba(216,178,62,0.55)",
         }}>
      <div className="flex items-center justify-between px-3 py-2 border-b border-[#D8B23E]/25">
        <div className="text-[9px] tracking-[0.28em] uppercase text-[#D8B23E] flex items-center gap-1.5 font-cinzel">
          <TrendingUp size={9} /> Vinci Ledger Pulse
        </div>
        <div className="text-[8px] tracking-[0.22em] uppercase text-[#8A6A1A]">
          {label}
        </div>
      </div>
      <div className="px-3 py-3 flex items-baseline justify-between">
        <div className="font-cinzel text-[22px] leading-none oracle-shimmer">{inr}</div>
        <div className="text-[10px] tracking-[0.22em] uppercase text-[#8A8A82]">/ {unit}</div>
      </div>
      <div className="relative h-[3px] bg-black/60">
        <span className="absolute inset-y-0 left-0 w-1/3 bg-gradient-to-r from-transparent via-[#F6E4A6] to-transparent"
              style={{ animation: "oracle-shimmer-move 2.6s linear infinite" }} />
      </div>
    </div>
  );
}

/* ── Vinci Code entry (guest → partner unlock) ──────────────────── */
function VinciCodeGate({ onVerified, codeInputRef }) {
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);
  const submit = async () => {
    const c = code.trim().toUpperCase();
    if (!c) return;
    setBusy(true);
    try {
      const resp = await fetch(`${API}/executive/verify-code`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ code: c }),
      });
      const data = await resp.json().catch(() => ({}));
      if (!resp.ok) {
        toast.error(data?.detail || "Vinci Code not recognised");
        return;
      }
      saveAuth(data);
      onVerified(data);
      toast.success(`Session verified · ${data.vinci_code}`);
    } catch {
      toast.error("Unable to reach the Vinci Oracle right now");
    } finally { setBusy(false); }
  };
  return (
    <div className="relative z-10 mx-4 mb-2 rounded-sm border border-[#D8B23E]/40 bg-black/50 px-3 py-2.5">
      <div className="flex items-center gap-1.5 mb-1.5">
        <KeyRound size={11} className="text-[#D8B23E]" />
        <span className="font-cinzel text-[9px] uppercase tracking-[0.30em] text-[#D8B23E]">
          Authorize with your Vinci Code
        </span>
      </div>
      <div className="flex gap-2">
        <input
          ref={codeInputRef}
          data-testid="virtual-exec-code-input"
          value={code}
          onChange={(e) => setCode(e.target.value.toUpperCase())}
          onKeyDown={(e) => e.key === "Enter" && !busy && submit()}
          placeholder="VN-5151-XXXX"
          spellCheck={false}
          className="flex-1 bg-black/60 border border-white/20 rounded-sm px-2.5 py-1.5 text-[12px] tracking-widest text-[#F5F5F0] uppercase focus:border-[#D8B23E] outline-none placeholder:text-[#8A6A1A]/70"
        />
        <button
          data-testid="virtual-exec-code-submit"
          onClick={submit}
          disabled={busy}
          className="font-cinzel text-[9px] uppercase tracking-[0.28em] bg-[#D8B23E] text-black px-3 rounded-sm disabled:opacity-60"
        >
          {busy ? <Loader2 size={12} className="animate-spin" /> : "Sync"}
        </button>
      </div>
      <a href="/onboarding"
         className="mt-1.5 block font-cinzel text-[8px] uppercase tracking-[0.28em] text-[#8A6A1A] hover:text-[#D8B23E]">
        No code? Apply at realtymaze.com/onboarding →
      </a>
    </div>
  );
}

/* ── VINCI LOCKDOWN · high-security refusal overlay ─────────────── */
function VinciLockdown({ onDismiss, onRequestEntry }) {
  return (
    <div
      data-testid="virtual-exec-lockdown"
      className="absolute inset-0 z-[92] flex items-center justify-center overflow-hidden"
      style={{
        background: "radial-gradient(circle at 50% 45%, rgba(160,12,12,0.55) 0%, rgba(20,4,4,0.92) 65%, rgba(6,2,2,0.96) 100%)",
        animation: "lockdown-red-pulse 1.4s ease-in-out infinite",
      }}
    >
      {/* Static bands */}
      {[18, 32, 46, 60, 74, 88].map((top, i) => (
        <span key={top}
              className="absolute left-0 right-0 h-[2px] bg-red-500/25 lockdown-band"
              style={{ top: `${top}%`, animationDelay: `${i * 120}ms` }} />
      ))}
      {/* Traveling scanline */}
      <span className="absolute left-0 right-0 h-[3px] bg-gradient-to-b from-transparent via-red-400/70 to-transparent lockdown-scan" />

      {/* Core content */}
      <div className="relative z-10 text-center px-6 py-5 max-w-[340px]">
        <div className="mx-auto mb-3 w-16 h-16 flex items-center justify-center">
          <ShieldAlert size={62} className="lockdown-shield text-red-400" strokeWidth={1.4} />
        </div>
        <div className="font-cinzel text-[10px] tracking-[0.42em] uppercase text-red-300 mb-1">
          Vinci Executive Command
        </div>
        <div className="lockdown-glitch font-cinzel text-[15px] tracking-[0.22em] uppercase text-red-100"
             style={{ lineHeight: 1.4 }}>
          Access Status: Denied
        </div>
        <div className="lockdown-glitch font-cinzel text-[10px] tracking-[0.32em] uppercase text-red-200/90 mt-2"
             style={{ animationDelay: "180ms" }}>
          Elite Data Requires 5151 Authorization
        </div>
        <div className="mt-4 flex flex-col gap-2 items-center">
          <button
            data-testid="virtual-exec-lockdown-cta"
            onClick={onRequestEntry}
            className="font-cinzel text-[10px] uppercase tracking-[0.32em] text-black px-5 py-2 rounded-sm"
            style={{
              background: "linear-gradient(90deg, #B87333 0%, #F6E4A6 50%, #D8B23E 100%)",
              boxShadow: "0 0 22px rgba(216,178,62,0.6), 0 0 44px rgba(216,178,62,0.25)",
            }}
          >
            Request 5151 Series Entry
          </button>
          <button
            data-testid="virtual-exec-lockdown-dismiss"
            onClick={onDismiss}
            className="font-cinzel text-[8px] uppercase tracking-[0.32em] text-red-200/60 hover:text-red-100"
          >
            Acknowledge · Return to Oracle
          </button>
        </div>
      </div>
    </div>
  );
}

/* ── Root component ─────────────────────────────────────────────── */
export function VirtualExecutive() {
  const [open, setOpen] = useState(false);
  const [minimised, setMinimised] = useState(false);
  const [messages, setMessages] = useState([]);
  const [pending, setPending] = useState(false);
  const [input, setInput] = useState("");
  const [auth, setAuth] = useState(null);         // {vinci_code, partner_name, verified_until}
  const [listening, setListening] = useState(false);
  const [mouse, setMouse] = useState(null);
  const [lockdown, setLockdown] = useState(false);   // Vinci Lockdown overlay
  const scrollRef = useRef(null);
  const windowRef = useRef(null);
  const recognitionRef = useRef(null);
  const codeInputRef = useRef(null);

  useEffect(() => { setAuth(loadAuth()); }, []);

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages, pending]);

  useEffect(() => () => {
    try { recognitionRef.current?.stop(); } catch { /* silent */ }
  }, []);

  const sessionId = useCallback(() => {
    try {
      let s = sessionStorage.getItem(SS_SID);
      if (!s) {
        s = "exec-" + Math.random().toString(36).slice(2) + Date.now().toString(36);
        sessionStorage.setItem(SS_SID, s);
      }
      return s;
    } catch { return "exec-anon"; }
  }, []);

  const openBubble = () => {
    setOpen(true);
    if (messages.length === 0) {
      send("Deliver your Sovereign greeting now.", { silent: true });
    }
  };

  const handleMouseMove = (e) => {
    const el = windowRef.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    setMouse({
      x: ((e.clientX - rect.left) / rect.width) * 100,
      y: ((e.clientY - rect.top) / rect.height) * 100,
    });
  };

  const onCodeVerified = (data) => {
    setAuth(data);
    // Reset conversation so the Oracle greets as VERIFIED
    setMessages([]);
    try { sessionStorage.removeItem(SS_SID); } catch { /* silent */ }
    setTimeout(() => send("Deliver your Sovereign greeting now.", { silent: true }), 60);
  };

  const revokeAuth = () => {
    clearAuth();
    setAuth(null);
    setMessages([]);
    try { sessionStorage.removeItem(SS_SID); } catch { /* silent */ }
    toast.message("Session cleared · returned to guest access");
  };

  const send = async (text, { silent = false } = {}) => {
    const msg = (text ?? input).trim();
    if (!msg) return;
    if (!silent) {
      setMessages((m) => [...m, { role: "user", content: msg }]);
      setInput("");
    }
    setPending(true);

    let assistantIdx = -1;
    setMessages((m) => {
      assistantIdx = m.length;
      return [...m, { role: "assistant", content: "", streaming: true }];
    });

    const appendToken = (chunk) => {
      setMessages((m) => {
        const copy = m.slice();
        if (assistantIdx >= 0 && copy[assistantIdx]) {
          copy[assistantIdx] = {
            ...copy[assistantIdx],
            content: copy[assistantIdx].content + chunk,
          };
        }
        return copy;
      });
    };

    const attachMeta = (meta) => {
      setMessages((m) => {
        const copy = m.slice();
        if (assistantIdx >= 0 && copy[assistantIdx]) {
          copy[assistantIdx] = {
            ...copy[assistantIdx],
            rfq_ref: meta?.rfq_ref,
            tool_events: meta?.tool_events,
            denied: meta?.denied,
            streaming: false,
          };
        }
        return copy;
      });
      if (meta?.rfq_ref) toast.success(`Draft RFQ created · ${meta.rfq_ref}`);
      if (meta?.denied) {
        // Fire the Vinci Lockdown after the reply finishes streaming
        setTimeout(() => setLockdown(true), 260);
      }
    };

    const markError = (detail) => {
      setMessages((m) => {
        const copy = m.slice();
        if (assistantIdx >= 0 && copy[assistantIdx]) {
          copy[assistantIdx] = {
            role: "assistant", content: detail, error: true, streaming: false,
          };
        }
        return copy;
      });
    };

    try {
      const resp = await fetch(`${API}/executive/chat/stream`, {
        method: "POST",
        headers: { "Content-Type": "application/json", "Accept": "text/event-stream" },
        body: JSON.stringify({
          session_id: sessionId(),
          message: msg,
          vinci_code: auth?.vinci_code || undefined,
        }),
      });

      if (!resp.ok || !resp.body) {
        let detail = "Oracle is momentarily recalibrating.";
        try { const j = await resp.json(); detail = j.detail || detail; } catch { /* silent */ }
        if (resp.status === 429) toast.error("Guest quota exhausted for this hour");
        markError(detail);
        return;
      }

      const reader = resp.body.getReader();
      const decoder = new TextDecoder();
      let buffer = "";
      let sawToken = false;

      while (true) {
        const { value, done } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        let idx;
        while ((idx = buffer.indexOf("\n\n")) !== -1) {
          const frame = buffer.slice(0, idx);
          buffer = buffer.slice(idx + 2);
          let eventName = "message";
          let dataLine = "";
          for (const line of frame.split("\n")) {
            if (line.startsWith("event:")) eventName = line.slice(6).trim();
            else if (line.startsWith("data:")) dataLine += line.slice(5).trim();
          }
          if (!dataLine) continue;
          let payload;
          try { payload = JSON.parse(dataLine); } catch { continue; }

          if (eventName === "token") { appendToken(payload.text || ""); sawToken = true; }
          else if (eventName === "meta") attachMeta(payload);
          else if (eventName === "error") {
            if (payload.status === 429) toast.error("Guest quota exhausted for this hour");
            markError(payload.detail || "Oracle is momentarily recalibrating.");
          } else if (eventName === "done" && !sawToken) {
            markError("Oracle returned no content.");
          }
        }
      }
    } catch { markError("Oracle is momentarily recalibrating."); }
    finally { setPending(false); }
  };

  const toggleVoice = () => {
    if (!VOICE_SUPPORTED) { toast.error("Voice input isn't supported in this browser"); return; }
    if (listening) { try { recognitionRef.current?.stop(); } catch { /* silent */ } return; }
    try {
      const rec = new SpeechRecognitionCtor();
      rec.lang = "en-IN";
      rec.interimResults = true;
      rec.continuous = false;
      rec.maxAlternatives = 1;
      let finalTranscript = "";
      rec.onresult = (ev) => {
        let interim = "";
        for (let i = ev.resultIndex; i < ev.results.length; i++) {
          const r = ev.results[i];
          if (r.isFinal) finalTranscript += r[0].transcript;
          else interim += r[0].transcript;
        }
        setInput((finalTranscript + interim).trim());
      };
      rec.onerror = (ev) => {
        setListening(false);
        if (ev.error === "not-allowed" || ev.error === "service-not-allowed") toast.error("Microphone access denied");
        else if (ev.error !== "no-speech" && ev.error !== "aborted") toast.error(`Voice input error · ${ev.error}`);
      };
      rec.onend = () => {
        setListening(false);
        const finalText = finalTranscript.trim();
        if (finalText.length >= 2) { setInput(""); send(finalText); }
      };
      recognitionRef.current = rec;
      rec.start();
      setListening(true);
    } catch { setListening(false); toast.error("Unable to start voice input"); }
  };

  const verifiedLabel = auth
    ? `${auth.vinci_code} · Verified 24h`
    : "Guest access · price data locked";

  return (
    <>
      {!open && (
        <motion.button
          data-testid="virtual-exec-launcher"
          initial={{ scale: 0, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          transition={{ delay: 1.2, type: "spring", stiffness: 210 }}
          onClick={openBubble}
          className="fixed bottom-6 right-6 z-[80] group"
          aria-label="Summon The Vinci Oracle"
        >
          <div className="relative w-[72px] h-[72px] flex items-center justify-center">
            <span className="absolute inset-0 rounded-full"
                  style={{
                    background: "radial-gradient(circle, rgba(216,178,62,0.55) 0%, rgba(216,178,62,0.18) 40%, transparent 70%)",
                    animation: "oracle-heartbeat 2.4s ease-in-out infinite",
                  }} />
            <span className="absolute inset-0 rounded-full border border-[#D8B23E]/50"
                  style={{ borderStyle: "dashed", animation: "oracle-iris-spin 12s linear infinite" }} />
            <span className="absolute inset-1.5 rounded-full border border-[#D8B23E]/25"
                  style={{ animation: "oracle-iris-spin-rev 22s linear infinite" }} />
            <span className="relative h-11 w-11 rounded-full flex items-center justify-center border-2 border-[#D8B23E]"
                  style={{
                    background: "radial-gradient(circle at 35% 35%, #241A08 0%, #050505 70%)",
                    boxShadow: "inset 0 0 14px rgba(216,178,62,0.5)",
                  }}>
              <span className="font-cinzel text-[15px] text-[#F6E4A6]"
                    style={{ textShadow: "0 0 12px rgba(246,228,166,0.9)" }}>V</span>
              <span className="absolute inset-1 rounded-full pointer-events-none"
                    style={{
                      background: "conic-gradient(from 0deg, transparent 0deg, rgba(246,228,166,0.35) 30deg, transparent 60deg)",
                      animation: "oracle-iris-spin 4s linear infinite",
                      mixBlendMode: "screen",
                    }} />
            </span>
            <span className={`absolute -top-0.5 -right-0.5 h-3 w-3 rounded-full border-2 border-black ${auth ? "bg-[#F6E4A6]" : "bg-[#2FB999]"}`}>
              <span className={`absolute inset-0 rounded-full animate-ping opacity-70 ${auth ? "bg-[#F6E4A6]" : "bg-[#2FB999]"}`} />
            </span>
          </div>
          <div className="absolute right-full top-1/2 -translate-y-1/2 mr-3 opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap px-3 py-1.5 bg-[#0A0906]/80 border border-[#D8B23E]/50 rounded-sm font-cinzel text-[9px] tracking-[0.32em] uppercase text-[#F6E4A6]">
            {auth ? `Verified · ${auth.vinci_code}` : "The Vinci Oracle"}
          </div>
        </motion.button>
      )}

      <AnimatePresence>
        {open && (
          <motion.div
            ref={windowRef}
            data-testid="virtual-exec-window"
            onMouseMove={handleMouseMove}
            onMouseLeave={() => setMouse(null)}
            initial={{ opacity: 0, scale: 0.2, rotate: 120, filter: "blur(20px)" }}
            animate={{ opacity: 1, scale: 1, rotate: 0, filter: "blur(0px)" }}
            exit={{ opacity: 0, scale: 0.4, rotate: -60, filter: "blur(18px)" }}
            transition={{ type: "spring", stiffness: 170, damping: 22 }}
            className={`oracle-window fixed z-[85] flex flex-col overflow-hidden rounded-sm
                          ${minimised ? "bottom-6 right-6 h-14 w-72" : "bottom-6 right-6 w-[400px] h-[620px] max-h-[90vh]"}`}
            style={{
              background: "linear-gradient(140deg, rgba(6,5,3,0.62) 0%, rgba(10,9,6,0.48) 45%, rgba(6,5,3,0.62) 100%)",
              backdropFilter: "blur(24px) saturate(140%)",
              WebkitBackdropFilter: "blur(24px) saturate(140%)",
            }}
          >
            {!minimised && <ParticleField mouse={mouse} />}

            {/* Vinci Lockdown overlay */}
            <AnimatePresence>
              {lockdown && !minimised && (
                <motion.div
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  transition={{ duration: 0.35 }}
                  className="absolute inset-0 z-[92]"
                >
                  <VinciLockdown
                    onDismiss={() => setLockdown(false)}
                    onRequestEntry={() => {
                      setLockdown(false);
                      setTimeout(() => codeInputRef.current?.focus(), 260);
                    }}
                  />
                </motion.div>
              )}
            </AnimatePresence>

            {/* Header */}
            <div className="relative flex items-center justify-between px-4 py-3 border-b border-[#D8B23E]/25"
                 style={{ background: "linear-gradient(90deg, rgba(15,12,5,0.75) 0%, rgba(36,26,8,0.55) 50%, rgba(15,12,5,0.75) 100%)" }}>
              <div className="flex items-center gap-3">
                <div className="relative h-10 w-10 flex items-center justify-center">
                  <span className="absolute inset-0 rounded-full border border-[#D8B23E]/60"
                        style={{ borderStyle: "dashed", animation: "oracle-iris-spin 10s linear infinite" }} />
                  <span className="h-7 w-7 rounded-full flex items-center justify-center border border-[#D8B23E]"
                        style={{ background: "radial-gradient(circle at 35% 35%, #241A08 0%, #050505 70%)",
                                   boxShadow: "inset 0 0 8px rgba(216,178,62,0.6)" }}>
                    <Sparkles className="text-[#F6E4A6]" size={12} />
                  </span>
                  <span className={`absolute -top-0.5 -right-0.5 h-2 w-2 rounded-full ${auth ? "bg-[#F6E4A6]" : "bg-[#2FB999]"}`}>
                    <span className={`absolute inset-0 rounded-full animate-ping opacity-80 ${auth ? "bg-[#F6E4A6]" : "bg-[#2FB999]"}`} />
                  </span>
                </div>
                <div>
                  <div className="text-[8px] tracking-[0.32em] uppercase text-[#8A6A1A] font-cinzel">
                    Neural Command · Live
                  </div>
                  <div className="font-cinzel text-[13px] uppercase tracking-[0.24em] oracle-shimmer leading-tight">
                    The Vinci Oracle
                  </div>
                </div>
              </div>
              <div className="flex items-center gap-1">
                <button onClick={() => setMinimised((m) => !m)}
                        className="text-[#8A8A82] hover:text-[#D8B23E] p-1 text-[10px] font-cinzel uppercase tracking-[0.22em]">
                  {minimised ? "↑" : "–"}
                </button>
                <button data-testid="virtual-exec-close"
                        onClick={() => setOpen(false)}
                        className="text-[#8A8A82] hover:text-[#D8B23E] p-1">
                  <X size={16} />
                </button>
              </div>
            </div>

            {/* Access-status strip */}
            {!minimised && (
              <div data-testid="virtual-exec-access-strip"
                   className={`relative z-10 flex items-center justify-between px-4 py-1.5 text-[9px] font-cinzel uppercase tracking-[0.26em] border-b border-[#D8B23E]/20
                                ${auth ? "text-[#F6E4A6]" : "text-[#8A6A1A]"}`}>
                <span className="flex items-center gap-1.5">
                  {auth ? <ShieldCheck size={11} /> : <KeyRound size={11} />}
                  {verifiedLabel}
                </span>
                {auth && (
                  <button onClick={revokeAuth}
                          data-testid="virtual-exec-signout"
                          className="flex items-center gap-1 hover:text-[#D8B23E]">
                    <LogOut size={10} /> Sign Out
                  </button>
                )}
              </div>
            )}

            {!minimised && (
              <>
                <div ref={scrollRef}
                     data-testid="virtual-exec-messages"
                     className="relative flex-1 overflow-y-auto px-4 py-4 space-y-3 z-10">
                  {messages.length === 0 && !pending && (
                    <div className="text-center py-8">
                      <div className="font-cinzel text-[10px] tracking-[0.42em] uppercase text-[#8A6A1A]">
                        Veni · Vidi · Vici
                      </div>
                      <p className="text-[12px] text-[#C7C7BF] font-body mt-3 max-w-xs mx-auto italic">
                        You stand before the Vinci Oracle — neural gateway to the world's most synchronized trade ecosystem. State your mandate.
                      </p>
                    </div>
                  )}
                  {messages.map((m, i) => <OracleMessage key={i} m={m} />)}
                  {pending && messages[messages.length - 1]?.streaming && !messages[messages.length - 1]?.content && (
                    <DataPulseWave />
                  )}
                </div>

                {/* Quick actions differ by tier */}
                {messages.length <= 1 && !pending && (
                  <div className="relative z-10 px-4 pb-2 flex flex-wrap gap-1.5">
                    {(auth ? [
                      "Today's rate for Tata Tiscon 12mm",
                      "Draft an RFQ for 120 MT cement",
                      "Confirm my 5151 tier",
                    ] : [
                      "What is Vinci Inc.?",
                      "How does the 5151 Programme work?",
                      "Tell me about your Big-Cap projects",
                    ]).map((q) => (
                      <button key={q}
                              onClick={() => send(q)}
                              className="font-cinzel text-[9px] uppercase tracking-[0.22em] border border-[#D8B23E]/40 text-[#D8B23E] hover:bg-[#D8B23E]/10 px-2.5 py-1 rounded-full">
                        {q}
                      </button>
                    ))}
                  </div>
                )}

                {/* Vinci Code gate — only when not authorized */}
                {!auth && <VinciCodeGate onVerified={onCodeVerified} codeInputRef={codeInputRef} />}

                {/* Input row */}
                <div className="relative z-10 px-4 py-3 border-t border-[#D8B23E]/25 flex items-center gap-2"
                     style={{ background: "linear-gradient(0deg, rgba(15,12,5,0.6), rgba(6,5,3,0.2))" }}>
                  {VOICE_SUPPORTED && (
                    <button
                      data-testid="virtual-exec-voice"
                      onClick={toggleVoice}
                      disabled={pending}
                      aria-label={listening ? "Stop voice input" : "Speak to the Oracle"}
                      title={listening ? "Listening… tap to stop" : "Speak to the Oracle"}
                      className={`relative h-9 w-9 rounded-sm border flex items-center justify-center transition-colors
                                  ${listening
                                    ? "border-[#C0392B] text-[#F6E4A6] bg-[#C0392B]/25"
                                    : "border-[#D8B23E]/50 text-[#D8B23E] hover:bg-[#D8B23E]/10"}`}
                    >
                      {listening ? <MicOff size={14} /> : <Mic size={14} />}
                      {listening && <span className="absolute inset-0 rounded-sm border border-[#C0392B] animate-ping" />}
                    </button>
                  )}
                  <input data-testid="virtual-exec-input"
                         value={input}
                         onChange={(e) => setInput(e.target.value)}
                         onKeyDown={(e) => e.key === "Enter" && !pending && send()}
                         placeholder={listening ? "Listening…" : "Address the Oracle…"}
                         className="flex-1 bg-black/60 border border-white/20 rounded-sm px-3 py-2 text-sm text-[#F5F5F0] focus:border-[#D8B23E] outline-none placeholder:text-[#8A6A1A]" />
                  <button data-testid="virtual-exec-send"
                          onClick={() => send()} disabled={pending}
                          className="bg-gradient-to-r from-[#D8B23E] via-[#F6E4A6] to-[#B87333] text-black px-3 py-2 rounded-sm">
                    {pending ? <Loader2 size={14} className="animate-spin" /> : <Send size={14} />}
                  </button>
                </div>
              </>
            )}
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}

function OracleMessage({ m }) {
  const isUser = m.role === "user";
  const priceEvent = useMemo(() => {
    if (isUser || !Array.isArray(m.tool_events)) return null;
    return m.tool_events.find(
      (ev) => ev?.tool === "get_price" && ev?.result?.found
    ) || null;
  }, [isUser, m.tool_events]);

  return (
    <div className={`flex ${isUser ? "justify-end" : "justify-start"} oracle-teleport`}>
      <div className={`max-w-[86%] px-3 py-2 rounded-sm text-[13px] font-body leading-relaxed
                        ${isUser
                          ? "bg-[#D8B23E]/12 border border-[#D8B23E]/40 text-[#F5F5F0]"
                          : m.error
                              ? "bg-[#C0392B]/12 border border-[#C0392B]/45 text-[#F5F5F0]"
                              : "border border-[#D8B23E]/25 text-[#F5F5F0]"}`}
           style={!isUser && !m.error ? {
             background: "linear-gradient(135deg, rgba(15,12,5,0.55) 0%, rgba(6,5,3,0.35) 100%)",
             boxShadow: "inset 0 0 20px rgba(216,178,62,0.06), 0 0 24px -14px rgba(216,178,62,0.4)",
             textShadow: "0 0 6px rgba(216,178,62,0.25)",
           } : undefined}>
        <span style={{ whiteSpace: "pre-wrap" }}>{m.content}</span>
        {m.streaming && m.content && (
          <span className="inline-block w-[6px] h-[12px] ml-0.5 align-middle bg-[#F6E4A6] animate-pulse"
                style={{ boxShadow: "0 0 10px #F6E4A6" }} />
        )}
        {priceEvent && <HolographicPricePlate priceEvent={priceEvent} />}
        {m.rfq_ref && (
          <div className="mt-2 pt-2 border-t border-[#D8B23E]/30 font-cinzel text-[9px] tracking-[0.32em] uppercase text-[#2FB999] flex items-center gap-1">
            <BadgeCheck size={10} /> Draft {m.rfq_ref}
          </div>
        )}
      </div>
    </div>
  );
}
